// src/pages/Marketplace/surge-pricing/EventsView.tsx
import React, { useState } from 'react';
import { ErrorBanner, Spinner, SuccessBanner } from '@/pages/Admin/shared';
import { useSurgeEventMutations, useSurgeEvents } from '@/pages/Marketplace/shared';
import type { Market, SurgeEvent } from '@/pages/Marketplace/shared';
import EventsList from './EventsList';
import EventsCalendar from './EventsCalendar';
import CreateEventModal from './CreateEventModal';

const LS_VIEW = 'marketplace.surge.view';
const LS_EXPIRED = 'marketplace.surge.showExpired';

interface Props { market: Market; apiUrl: string; accessToken: string | null; }

export default function EventsView({ market, apiUrl, accessToken }: Props): React.ReactElement {
  const [view, setView] = useState<'calendar' | 'list'>(
    () => (localStorage.getItem(LS_VIEW) as 'calendar' | 'list') || 'calendar',
  );
  const [showExpired, setShowExpired] = useState<boolean>(
    () => localStorage.getItem(LS_EXPIRED) === 'true',
  );
  const [editing, setEditing] = useState<SurgeEvent | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletedMsg, setDeletedMsg] = useState<string | null>(null);

  const { data: events, loading, error, refetch } =
    useSurgeEvents(apiUrl, accessToken, market.id, showExpired);
  const mutations = useSurgeEventMutations(apiUrl, accessToken);

  const handleDelete = async (e: SurgeEvent) => {
    if (!window.confirm(`Delete "${e.name}"? This is a soft delete and cannot be undone from the UI.`)) return;
    const ok = await mutations.softDelete(e.id);
    if (ok) { setDeletedMsg(`Deleted "${e.name}".`); refetch(); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-white">Event overrides</h2>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-gray-400">
            <input
              type="checkbox"
              checked={showExpired}
              onChange={(e) => {
                setShowExpired(e.target.checked);
                localStorage.setItem(LS_EXPIRED, String(e.target.checked));
              }}
            />
            Show expired
          </label>
          <div className="inline-flex border border-gray-700 rounded overflow-hidden">
            {(['calendar', 'list'] as const).map((v) => (
              <button
                key={v}
                onClick={() => { setView(v); localStorage.setItem(LS_VIEW, v); }}
                className={`px-3 py-1 text-xs ${view === v ? 'bg-violet-700 text-white' : 'bg-gray-900 text-gray-400'}`}
              >
                {v}
              </button>
            ))}
          </div>
          <button
            onClick={() => setCreating(true)}
            className="px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-700 rounded"
          >
            + Create Event
          </button>
        </div>
      </div>

      {loading && <Spinner size="sm" />}
      {error && <ErrorBanner message={error} />}
      {deletedMsg && <SuccessBanner message={deletedMsg} />}

      {view === 'list' && (
        <EventsList
          events={events ?? []}
          tz={market.timezone}
          onEdit={setEditing}
          onDelete={handleDelete}
        />
      )}

      {view === 'calendar' && (
        <EventsCalendar
          events={events ?? []}
          onSelect={(e) => setEditing(e)}
          onCreateAt={() => setCreating(true)}
        />
      )}

      {(creating || editing) && (
        <CreateEventModal
          market={market}
          editing={editing}
          apiUrl={apiUrl}
          accessToken={accessToken}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={refetch}
        />
      )}
    </div>
  );
}
